import React, { useState, useEffect } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Button } from '@frontend/shadcn/components/ui/button';
import { CircleInfoIcon, XmarkIcon, PlusIcon } from '@revfluence/fresh-icons/regular/esm';

interface FieldFormPopoverProps {
  onClose: () => void;
  onSave: (field: {
    name: string;
    type: string;
    campaignOnly: boolean;
    description?: string;
    options?: string[];
  }) => void;
  initialData?: {
    name: string;
    type: string;
    campaignOnly: boolean;
    description?: string;
    options?: string[];
  };
}

export const FieldFormPopover: React.FC<FieldFormPopoverProps> = ({
  onClose,
  onSave,
  initialData,
}) => {
  const [fieldName, setFieldName] = useState(initialData?.name || '');
  const [fieldType, setFieldType] = useState(initialData?.type || 'Free Text');
  const [campaignOnly, setCampaignOnly] = useState(initialData?.campaignOnly || false);
  const [checkboxDescription, setCheckboxDescription] = useState(initialData?.description || '');
  const [options, setOptions] = useState<string[]>(initialData?.options || ['']);

  const isSelectType = fieldType === 'Single Select' || fieldType === 'Multi Select';

  // Reset checkbox description when field type changes
  useEffect(() => {
    if (fieldType !== 'Checkbox') {
      setCheckboxDescription('');
    } else {
      // Restore initial description if switching back to checkbox
      setCheckboxDescription(initialData?.description || '');
    }
  }, [fieldType, initialData?.description]);

  // Reset options when field type changes
  useEffect(() => {
    if (!isSelectType) {
      setOptions(['']);
    } else {
      // Restore initial options if switching back to select type
      setOptions(initialData?.options || ['']);
    }
  }, [isSelectType, initialData?.options]);

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave({
      name: fieldName,
      type: fieldType,
      campaignOnly,
      ...(fieldType === 'Checkbox' && { description: checkboxDescription }),
      ...(isSelectType && { options: options.filter((opt) => opt.trim() !== '') }),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-[480px] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200">
          <span className="text-base font-medium text-gray-800">New Field</span>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
          >
            <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
          </button>
        </div>

        {/* Content */}
        <div className="self-stretch p-6 flex flex-col gap-4">
          {/* Field Name */}
          <div className="self-stretch flex flex-col">
            <div className="self-stretch pb-2 flex items-center gap-1">
              <div className="text-zinc-900 text-xs font-medium">Field Name</div>
            </div>
            <Input
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              className="h-8"
              placeholder="New Field Name Goes Here"
            />
          </div>

          {/* Field Type */}
          <div className="self-stretch flex flex-col">
            <div className="self-stretch pb-2 flex items-center gap-1">
              <div className="text-zinc-900 text-xs font-medium">Field Type</div>
            </div>
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select field type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Free Text">Free Text</SelectItem>
                <SelectItem value="Number">Number</SelectItem>
                <SelectItem value="Date">Date</SelectItem>
                <SelectItem value="Yes/No">Yes/No</SelectItem>
                <SelectItem value="Checkbox">Checkbox</SelectItem>
                <SelectItem value="Single Select">Single Select</SelectItem>
                <SelectItem value="Multi Select">Multi Select</SelectItem>
                <SelectItem value="Upload File">Upload File</SelectItem>
                <SelectItem value="URL">URL</SelectItem>
                <SelectItem value="Call to Action">Call to Action</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Checkbox Description - Only show for Checkbox type */}
          {fieldType === 'Checkbox' && (
            <div className="self-stretch flex flex-col">
              <div className="self-stretch pb-2 flex items-center gap-1">
                <div className="text-zinc-900 text-xs font-medium">Checkbox Description</div>
              </div>
              <Input
                value={checkboxDescription}
                onChange={(e) => setCheckboxDescription(e.target.value)}
                className="h-8"
                placeholder="Text appears next to checkbox"
              />
            </div>
          )}

          {/* Options section for Select/Multi Select */}
          {isSelectType && (
            <div className="flex flex-col gap-4">
              {options.map((option, index) => (
                <div key={index} className="flex flex-col gap-2">
                  <span className="text-zinc-900 text-xs font-medium">
                    Option {index + 1} Label
                  </span>
                  <div className="flex items-center gap-2">
                    <Input
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="h-8"
                    />
                    {options.length > 1 && (
                      <button
                        onClick={() => handleRemoveOption(index)}
                        className="w-8 h-8 p-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-red-200 flex justify-center items-center"
                      >
                        <XmarkIcon className="w-3.5 h-3.5 text-red-200" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                onClick={handleAddOption}
                className="flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-gray-600 mt-2"
                type="button"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                <span className="underline">Add option</span>
              </button>
            </div>
          )}

          {/* Campaign Only Toggle */}
          <div className="self-stretch flex flex-col gap-2">
            <div className="h-5 flex items-center gap-2">
              <Switch
                checked={campaignOnly}
                onCheckedChange={setCampaignOnly}
                className="data-[state=checked]:bg-gray-800"
              />
              <div className="flex items-center gap-1">
                <span className="text-gray-800 text-sm">Associate field to campaign only</span>
                <CircleInfoIcon className="w-3 h-3 text-gray-300" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-end items-center gap-2 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-4 py-1 h-8"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!fieldName || !fieldType || (fieldType === 'Checkbox' && !checkboxDescription)}
            className="px-4 py-1 h-8 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
          >
            Create Field
          </Button>
        </div>
      </div>
    </div>
  );
};
